import { z } from "zod";
import { isUUID } from "../helpers/uuid";
import { Taxonomy } from "../categories";

export enum TransferRuleDirection {
    FROM = "from",
    TO = "to",
}

export enum RuleConditionType {
    COUNTERPARTY = "counterparty",
    CATEGORY = "category",
    SUGGESTED_CATEGORY = "suggested_category",
    DESCRIPTION_IS = "description_is",
    DESCRIPTION_INCLUDES = "description_includes",
    DESCRIPTION_DOES_NOT_INCLUDE = "description_does_not_include",
    AMOUNT = "amount",
    DATE = "date",
    FINANCIAL_ACCOUNTS = "financial_accounts",
    SUBACCOUNTS = "subaccounts",
    ENTITY = "entity",
    DATE_PROXIMITY = "date_proximity",
    CLASS = "class",
    SIMILAR_TO = "similar_to",
    AMOUNT_IS_ROUND = "amount_is_round",
    AMOUNT_IS_NOT_ROUND = "amount_is_not_round",
}

export enum AmountRoundingLevel {
    DOLLAR = "dollar",
    TEN = "ten",
    HUNDRED = "hundred",
    THOUSAND = "thousand",
}

export const ROUNDING_LEVEL_LABELS: Record<AmountRoundingLevel, string> = {
    [AmountRoundingLevel.DOLLAR]: "to the dollar",
    [AmountRoundingLevel.TEN]: "to the nearest $10",
    [AmountRoundingLevel.HUNDRED]: "to the nearest $100",
    [AmountRoundingLevel.THOUSAND]: "to the nearest $1,000",
};

export enum RuleActionType {
    CATEGORY = "category",
    ENTITY = "entity",
    ACCOUNT_ID = "account_id",
    ACCOUNT_NAME = "account_name",
    ACCRUAL_ACCOUNT_ID = "accrual_account_id",
    ACCRUAL_ACCOUNT_NAME = "accrual_account_name",
    CLASS = "class",
    MEMO = "memo",
    COUNTERPARTY = "counterparty",
    CUSTOMER = "customer",
    SPLIT = "split",
    TRANSFER_MATCHING = "transfer_matching",
    ASSIGN_TO_SCHEDULE = "assign_to_schedule",
}

export enum RuleGroupType {
    TRANSACTION = "transaction",
    ACCOUNTING = "accounting",
    TRANSFER = "transfer",
    SPLIT = "split",
}

export const BusinessRuleActions = [
    RuleActionType.CATEGORY,
    RuleActionType.ENTITY,
    RuleActionType.CLASS,
    RuleActionType.COUNTERPARTY,
    RuleActionType.CUSTOMER,
    RuleActionType.MEMO,
    RuleActionType.ACCOUNT_ID,
    RuleActionType.ACCOUNT_NAME,
    RuleActionType.ACCRUAL_ACCOUNT_ID,
    RuleActionType.ACCRUAL_ACCOUNT_NAME,
    RuleActionType.ASSIGN_TO_SCHEDULE,
];
export const AccountingRuleActions = [
    RuleActionType.ACCOUNT_ID,
    RuleActionType.ACCOUNT_NAME,
    RuleActionType.ACCRUAL_ACCOUNT_ID,
    RuleActionType.ACCRUAL_ACCOUNT_NAME,
];

const RULE_ACTION_DISPLAY_PRIORITY: RuleActionType[] = [
    RuleActionType.CATEGORY,
    RuleActionType.ACCOUNT_ID,
    RuleActionType.ACCOUNT_NAME,
    RuleActionType.ACCRUAL_ACCOUNT_ID,
    RuleActionType.ACCRUAL_ACCOUNT_NAME,
    RuleActionType.COUNTERPARTY,
    RuleActionType.CUSTOMER,
    RuleActionType.CLASS,
    RuleActionType.ASSIGN_TO_SCHEDULE,
];

interface TransactionRuleActionSortable {
    actionType: RuleActionType;
}

export function compareTransactionRuleActions(
    left: TransactionRuleActionSortable,
    right: TransactionRuleActionSortable,
): number {
    const leftPriority = RULE_ACTION_DISPLAY_PRIORITY.indexOf(left.actionType);
    const rightPriority = RULE_ACTION_DISPLAY_PRIORITY.indexOf(
        right.actionType,
    );
    const fallbackPriority = RULE_ACTION_DISPLAY_PRIORITY.length;
    const normalizedLeftPriority =
        leftPriority === -1 ? fallbackPriority : leftPriority;
    const normalizedRightPriority =
        rightPriority === -1 ? fallbackPriority : rightPriority;

    return normalizedLeftPriority - normalizedRightPriority;
}

export function sortTransactionRuleActions<
    TAction extends TransactionRuleActionSortable,
>(actions: readonly TAction[]): TAction[] {
    return [...actions].sort(compareTransactionRuleActions);
}

export function filterVisibleRuleActions<
    TAction extends TransactionRuleActionSortable,
>(actions: readonly TAction[], options: { isGlFirst: boolean }): TAction[] {
    if (!options.isGlFirst) {
        return [...actions];
    }
    return actions.filter(
        (action) => action.actionType !== RuleActionType.CATEGORY,
    );
}

export function isRuleHiddenForGlFirst(
    rule: { actions?: TransactionRuleActionSortable[] },
    options: { isGlFirst: boolean },
): boolean {
    if (!options.isGlFirst) {
        return false;
    }
    const actions = rule.actions ?? [];
    return (
        actions.length > 0 &&
        actions.every((action) => action.actionType === RuleActionType.CATEGORY)
    );
}

export interface AccrualAccountActionValue {
    accountId?: string;
    accountName?: string;
    accountIds?: string[];
    autoConfirm: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readAccrualAccountObject(
    obj: Record<string, unknown>,
): AccrualAccountActionValue {
    const accountId =
        "accountId" in obj && typeof obj.accountId === "string"
            ? obj.accountId
            : undefined;
    const accountName =
        "accountName" in obj && typeof obj.accountName === "string"
            ? obj.accountName
            : undefined;
    const accountIdsField =
        "accountIds" in obj && Array.isArray(obj.accountIds)
            ? obj.accountIds.filter(
                  (value): value is string => typeof value === "string",
              )
            : undefined;
    const legacyAccountIds = accountId !== undefined ? [accountId] : undefined;
    const accountIds = accountIdsField ?? legacyAccountIds;
    const autoConfirm = "autoConfirm" in obj && obj.autoConfirm === true;
    return { accountId, accountName, accountIds, autoConfirm };
}

const AccountIdActionValueSchema = z.union([
    z.string().uuid(),
    z.array(z.string().uuid()),
]);

export function parseAccountIdActionValue(raw: unknown): string[] | null {
    const result = AccountIdActionValueSchema.safeParse(raw);
    if (!result.success) {
        return null;
    }
    return Array.isArray(result.data) ? result.data : [result.data];
}

/**
 * `parseAccrualAccountActionValue` fills `accountIds` from a scalar
 * `accountId`, so multi-business provenance has to be read from the raw value.
 */
export function hasExplicitAccrualAccountIds(raw: unknown): boolean {
    let parsed: unknown = raw;
    if (typeof raw === "string") {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return false;
        }
    }
    return isPlainObject(parsed) && Array.isArray(parsed.accountIds);
}

export function parseAccrualAccountActionValue(
    raw: unknown,
): AccrualAccountActionValue {
    if (typeof raw === "string") {
        try {
            const parsed: unknown = JSON.parse(raw);
            if (isPlainObject(parsed)) {
                return readAccrualAccountObject(parsed);
            }
        } catch {
            // Fall through to legacy handling.
        }
        return { accountId: raw, accountIds: [raw], autoConfirm: false };
    }
    if (isPlainObject(raw)) {
        return readAccrualAccountObject(raw);
    }
    return { autoConfirm: false };
}

export function serializeAccrualAccountActionValue(
    value: AccrualAccountActionValue,
): string {
    return JSON.stringify(value);
}

export const AssignToScheduleActionValueSchema = z
    .object({
        scheduleTemplateId: z.string().uuid(),
        autoPostOnApply: z.boolean().default(false),
        autoPostRecognition: z.boolean().default(false),
    })
    .strict();

export type AssignToScheduleActionValue = z.infer<
    typeof AssignToScheduleActionValueSchema
>;

export function parseAssignToScheduleActionValue(
    raw: TransactionRuleActionDefinition["value"],
): AssignToScheduleActionValue | null {
    if (typeof raw === "string") {
        if (isUUID(raw)) {
            return {
                scheduleTemplateId: raw,
                autoPostOnApply: false,
                autoPostRecognition: false,
            };
        }
        try {
            const result = AssignToScheduleActionValueSchema.safeParse(
                JSON.parse(raw),
            );
            return result.success ? result.data : null;
        } catch {
            return null;
        }
    }
    return null;
}

export function serializeAssignToScheduleActionValue(
    value: AssignToScheduleActionValue,
): string {
    return JSON.stringify(value);
}

export interface TransactionRuleActionDefinition {
    actionType: RuleActionType;
    value: string | number | string[];
    transferDirection: TransferRuleDirection | null;
}
export interface TransactionRuleAction extends TransactionRuleActionDefinition {
    id: string;
}

export enum RuleSource {
    SETTINGS = "settings",
    TRANSACTIONS_FEED = "transactions_feed",
    ACTIONS = "actions",
    ENTITY_MIGRATION = "entity_migration",
    LOAN_RECONCILIATION = "loan_reconciliation",
    NORMALIZATION = "normalization",
    PLATFORM = "platform",
}

export enum RuleStatus {
    ENABLED = "Enabled",
    ARCHIVED = "Archived",
}

export enum RuleReviewStatus {
    REVIEWED = "reviewed",
    UNREVIEWED = "unreviewed",
}

export enum RuleSourceFilter {
    CUSTOM = "custom",
    MATCHED_KICK = "matchedKick",
    UNMATCHED_KICK = "unmatchedKick",
}

export interface TransactionRuleGroup {
    id: string;
    workspaceId: string;
    rules?: TransactionRule[];
    name: string;
    icon: string;
    isDefault?: boolean;
    order: number;
    type: RuleGroupType;
}

export interface TransactionRule {
    id: string;
    workspaceId: string;
    groupId: string;
    conditions: TransactionRuleCondition[];
    actions: TransactionRuleAction[];
    source: RuleSource | null;
    order: number;
    isDisabled?: boolean | null;
    note: string | null;
}

export interface TransactionRuleApplication {
    ruleId: string;
    transactionId: number;
    actionType: RuleActionType;
}

export interface RuleConditionAmount {
    min?: number;
    max?: number;
}

export interface RuleConditionAmountRounding {
    level: AmountRoundingLevel;
    isCredit: boolean;
}

export interface RuleConditionDate {
    after?: string;
    before?: string;
}

export interface RuleConditionSimilarTo {
    normalizedDescriptionId: string;
    // null = plain (non-split) rows only; "wages" = that split type only
    effectiveSplitKey: string | null;
}

export interface TransactionRuleConditionDefinition {
    conditionType: RuleConditionType;
    value:
        | string
        | string[]
        | number
        | number[]
        | Taxonomy
        | RuleConditionAmount
        | RuleConditionDate
        | RuleConditionAmountRounding
        | RuleConditionSimilarTo;
    transferDirection: TransferRuleDirection | null;
}

export interface TransactionRuleCondition extends TransactionRuleConditionDefinition {
    id: string;
}
